import {
  Row,
  Col,
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Checkbox,
} from "antd";
import React, { useState, useEffect } from "react";
import useRequest from "../hooks/useRequest";
import { ShowToast, Severty } from "../helper/toast";
import Main from "../components/layout/Main";
import useDebounce from "../hooks/useDebounce";
import { ExclamationCircleOutlined } from "@ant-design/icons";
const { TextArea } = Input;
const Search = Input.Search;
const { Option } = Select;

function Location() {
  const { request } = useRequest();
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [list, setList] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const debouncedSearchText = useDebounce(searchText, 300);
  const [selected, setSelected] = useState();

  const columns = [
    {
      title: "Location Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Country",
      dataIndex: "country",
      key: "country",
      render: (_, { countryData }) => {
        return <div>{countryData ? countryData.name : "--"}</div>;
      },
    },
    {
      title: "State",
      dataIndex: "state",
      key: "state",
      render: (_, { stateData }) => {
        return <div>{stateData ? stateData.name : "--"}</div>;
      },
    },
    {
      title: "City",
      dataIndex: "city",
      key: "city",
      render: (_, { cityData }) => {
        return <div>{cityData ? cityData.name : "--"}</div>;
      },
    },
    {
      title: "Action",
      fixed: "right",
      render: (_, record) => {
        return (
          <div style={{ display: "flex", gap: "8px" }}>
            <Button
              className="tb-view"
              onClick={() => {
                setVisible(true);
                setSelected(record);
              }}
            >
              <i className="fa fa-light fa-pencil"></i> Edit
            </Button>
            <Button
              className="tb-view"
              danger
              onClick={() => handleDelete(record)}
            >
              <i className="fa fa-light"></i> Delete
            </Button>
          </div>
        );
      },
    },
  ];

  const onSearch = (e) => {
    setSearchText(e.target.value);
    setPagination({ current: 1 });
  };

  const { confirm } = Modal;

  const handleDelete = (record) => {
    setTimeout(() => {
      confirm({
        icon: <ExclamationCircleOutlined />,
        content: <Button>Are you sure you want to delete this location? </Button>,
        onOk() {
          deleteLocation(record);
        },
        onCancel() {
          console.log("Cancel");
        },
      });
    }, 5);
  };

  const deleteLocation = (record) => {
    setLoading(true);
    request({
      url: `/admin/location/delete-location/${record._id}`,
      method: "DELETE",
      onSuccess: (data) => {
        setLoading(false);
        setRefresh((prev) => !prev);
        ShowToast("Location deleted successfully", Severty.SUCCESS);
      },
      onError: (error) => {
        setLoading(false);
        ShowToast(error.response?.data?.message || "Failed to delete location", Severty.ERROR);
      },
    });
  };

  useEffect(() => {
    setLoading(true);
    fetchData(pagination);
  }, [refresh, debouncedSearchText]);

  const fetchData = (pagination, filters) => {
    request({
      url: `/admin/location/list?page=${pagination.current}&limit=${pagination.pageSize}&search=${debouncedSearchText}`,
      method: "GET",
      onSuccess: (data) => {
        setLoading(false);
        setList(data.data.docs);
        setPagination({
          current: pagination.current,
          total: data.data.totalDocs,
        });
      },
      onError: (error) => {
        ShowToast(error, Severty.ERROR);
      },
    });
  };

  const handleChange = (pagination, filters) => {
    fetchData(pagination, filters);
  };

  return (
    <Main>
      <div className="tabled">
        <Row>
          <Col xs="24" md={24} lg={24} xl={24}>
            <Card
              bordered={false}
              className="criclebox tablespace mb-24"
              title="Location Management"
              extra={
                <>
                  <Search
                    size="large"
                    onChange={onSearch}
                    value={searchText}
                    onPressEnter={onSearch}
                  />
                  <Button onClick={() => setVisible(true)}>Add Location</Button>
                </>
              }
            >
              <div className="table-responsive">
                <Table
                  loading={loading}
                  columns={columns}
                  dataSource={list}
                  pagination={pagination}
                  onChange={handleChange}
                  className="ant-border-space"
                />
              </div>
            </Card>
          </Col>
        </Row>
      </div>
      {visible && (
        <AddFrom
          show={visible}
          hide={() => {
            setSelected();
            setVisible(false);
          }}
          data={selected}
          refresh={() => setRefresh((prev) => !prev)}
        />
      )}
    </Main>
  );
}

const AddFrom = ({ show, hide, data, refresh }) => {
  const [form] = Form.useForm();
  const { request } = useRequest();
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    fetchCountries();
    if (data) {
      form.setFieldsValue({
        ...data,
        country: data.country ? String(data.country) : undefined,
        state: data.state ? String(data.state) : undefined,
        city: data.city ? String(data.city) : undefined
      });
      data.country && fetchStates(data.country);
      data.state && fetchCities(data.state);
    }
  }, [data]);
  const fetchCountries = () => {
    request({
      url: `/common/country`,
      method: "GET",
      onSuccess: (data) => {
        console.log(data.data);
        setCountries(data.data.data);
      },
      onError: (error) => {
        ShowToast(error, Severty.ERROR);
      },
    });
  };

  const fetchStates = (countryId) => {
    request({
      url: `/common/state/${countryId}`,
      method: "GET",
      onSuccess: (data) => {
        setStates(data.data.data);
        setCities([]); // Reset cities when country changes
      },
      onError: (error) => {
        ShowToast(error, Severty.ERROR);
      },
    });
  };

  const fetchCities = (stateId) => {
    request({
      url: `/common/city/${stateId}`,
      method: "GET",
      onSuccess: (data) => {
        setCities(data.data.data);
      },
      onError: (error) => {
        ShowToast(error, Severty.ERROR);
      },
    });
  };

  const onCreate = (values) => {
    setLoading(true);
    request({
      url: data
        ? `/admin/location/edit-location/${data._id}`
        : `/admin/location/add-location`,
      method: data ? "PUT" : "POST",
      data: {
        ...values,
        country: values.country ? Number(values.country) : undefined,
        state: values.state ? Number(values.state) : undefined,
        city: values.city ? Number(values.city) : undefined,
      },
      onSuccess: () => {
        setLoading(false);
        refresh();
        hide();
      },
      onError: (error) => {
        ShowToast(error.response.data.message, Severty.ERROR);
        setLoading(false);
      },
    });
  };

  return (
    <Modal
      visible={show}
      title={`${data ? "Edit" : "Create"} location`}
      onCancel={hide}
      okButtonProps={{
        form: "create",
        htmlType: "submit",
        loading: loading,
      }}
    >
      <Form id="create" form={form} onFinish={onCreate} layout="vertical">
        <Form.Item
          label="Location Name"
          name="name"
          rules={[
            { required: true, message: "Please enter the location name!" },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          label="Country"
          name="country"
          rules={[{ required: true, message: "Please select a country!" }]}
        >
          <Select showSearch placeholder="Select Country" optionFilterProp="children"
            filterOption={(input, option) =>
              option.children.toLowerCase().includes(input.toLowerCase())
            } onChange={(val) => {
              fetchStates(val)
              form.setFieldsValue({ city: undefined, state: undefined })
            }}>
            {countries.length > 0 &&
              countries.map((country) => (
                <Option key={country.id} value={country.id}>
                  {country.name}
                </Option>
              ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="State"
          name="state"
          rules={[{ required: true, message: "Please select a state!" }]}
        >
          <Select placeholder="Select State" onChange={fetchCities} showSearch optionFilterProp="children"
            filterOption={(input, option) =>
              option.children.toLowerCase().includes(input.toLowerCase())
            }>
            {states.length > 0 &&
              states.map((state) => (
                <Option key={state.id} value={state.id}>
                  {state.name}
                </Option>
              ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="City"
          name="city"
          rules={[{ required: true, message: "Please select a city!" }]}
        >
          <Select placeholder="Select City" showSearch optionFilterProp="children"
            filterOption={(input, option) =>
              option.children.toLowerCase().includes(input.toLowerCase())
            }>
            {cities.length > 0 &&
              cities.map((city) => (
                <Option key={city.id} value={city.id}>
                  {city.name}
                </Option>
              ))}
          </Select>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default Location;
